/**
 * Authentik OIDC Configuration
 *
 * This module handles OAuth2/OIDC authentication with Authentik SSO.
 * Uses PKCE (Proof Key for Code Exchange) for secure SPA authentication.
 */

// Authentik OIDC endpoints
export const AUTHENTIK_CONFIG = {
  authority: 'https://login.ab-civil.com/application/o/project-interface/',
  clientId: 'project-interface',
  redirectUri: `${window.location.origin}/callback`,
  postLogoutRedirectUri: window.location.origin,
  scope: 'openid email profile groups',

  // Endpoints
  authorizationEndpoint: 'https://login.ab-civil.com/application/o/authorize/',
  tokenEndpoint: 'https://login.ab-civil.com/application/o/token/',
  userinfoEndpoint: 'https://login.ab-civil.com/application/o/userinfo/',
  endSessionEndpoint: 'https://login.ab-civil.com/application/o/project-interface/end-session/',
  jwksUri: 'https://login.ab-civil.com/application/o/project-interface/jwks/',
};

/**
 * Generate a cryptographically random string for PKCE
 */
function generateRandomString(length) {
  const array = new Uint8Array(length);
  crypto.getRandomValues(array);
  return Array.from(array, byte => byte.toString(16).padStart(2, '0')).join('');
}

/**
 * Generate SHA-256 hash and base64url encode it for PKCE code challenge
 */
async function generateCodeChallenge(verifier) {
  const encoder = new TextEncoder();
  const data = encoder.encode(verifier);
  const digest = await crypto.subtle.digest('SHA-256', data);

  // Base64url encode the digest
  const base64 = btoa(String.fromCharCode(...new Uint8Array(digest)));
  return base64
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/, '');
}

/**
 * Generate a random state parameter for CSRF protection
 */
function generateState() {
  return generateRandomString(32);
}

/**
 * Store PKCE values in sessionStorage for use after redirect
 */
function storePKCEValues(codeVerifier, state) {
  sessionStorage.setItem('pkce_code_verifier', codeVerifier);
  sessionStorage.setItem('oauth_state', state);
}

/**
 * Retrieve and clear PKCE values from sessionStorage
 */
export function getPKCEValues() {
  const codeVerifier = sessionStorage.getItem('pkce_code_verifier');
  const state = sessionStorage.getItem('oauth_state');

  // Clear after retrieval for security
  sessionStorage.removeItem('pkce_code_verifier');
  sessionStorage.removeItem('oauth_state');

  return { codeVerifier, state };
}

/**
 * Initiate the OAuth2 authorization flow with PKCE
 * Redirects the user to Authentik login page
 */
export async function initiateLogin() {
  // Prevent duplicate login attempts within 2 seconds (double-click protection)
  const lastLoginAttempt = sessionStorage.getItem('login_attempt_timestamp');
  if (lastLoginAttempt) {
    const elapsed = Date.now() - parseInt(lastLoginAttempt, 10);
    if (elapsed < 2000) {
      console.log('[Auth] Login attempt blocked - duplicate within 2 seconds');
      return;
    }
  }
  sessionStorage.setItem('login_attempt_timestamp', Date.now().toString());

  // Generate PKCE values
  const codeVerifier = generateRandomString(64);
  const codeChallenge = await generateCodeChallenge(codeVerifier);
  const state = generateState();

  // Store for use after redirect
  storePKCEValues(codeVerifier, state);

  // Build authorization URL
  const params = new URLSearchParams({
    client_id: AUTHENTIK_CONFIG.clientId,
    redirect_uri: AUTHENTIK_CONFIG.redirectUri,
    response_type: 'code',
    scope: AUTHENTIK_CONFIG.scope,
    state: state,
    code_challenge: codeChallenge,
    code_challenge_method: 'S256',
  });

  const authUrl = `${AUTHENTIK_CONFIG.authorizationEndpoint}?${params.toString()}`;

  // Debug: log the full URL being used
  console.log('SSO Redirect URL:', authUrl);
  console.log('Redirect URI being sent:', AUTHENTIK_CONFIG.redirectUri);

  // Redirect to Authentik
  window.location.href = authUrl;
}

/**
 * Exchange authorization code for Authentik tokens directly
 * Then return user info for Supabase session creation
 */
export async function exchangeCodeForSession(code, codeVerifier) {
  // Exchange code for tokens directly with Authentik
  const tokenParams = new URLSearchParams({
    grant_type: 'authorization_code',
    client_id: AUTHENTIK_CONFIG.clientId,
    code: code,
    redirect_uri: AUTHENTIK_CONFIG.redirectUri,
    code_verifier: codeVerifier,
  });

  console.log('Exchanging code for tokens...');

  const tokenResponse = await fetch(AUTHENTIK_CONFIG.tokenEndpoint, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/x-www-form-urlencoded',
    },
    body: tokenParams.toString(),
  });

  if (!tokenResponse.ok) {
    const errorText = await tokenResponse.text();
    console.error('Token exchange failed:', errorText);
    throw new Error(`Token exchange failed: ${tokenResponse.status}`);
  }

  const tokens = await tokenResponse.json();
  console.log('Got tokens, fetching user info...');

  // Get user info from Authentik
  const userInfoResponse = await fetch(AUTHENTIK_CONFIG.userinfoEndpoint, {
    headers: {
      Authorization: `Bearer ${tokens.access_token}`,
    },
  });

  if (!userInfoResponse.ok) {
    throw new Error('Failed to get user info from Authentik');
  }

  const userInfo = await userInfoResponse.json();
  console.log('Got user info:', { email: userInfo.email, name: userInfo.name });

  return {
    success: true,
    user: userInfo,
    tokens: tokens,
  };
}

/**
 * Initiate logout from Authentik
 * Clears local session and redirects to Authentik end-session endpoint
 */
export function initiateLogout(idToken = null) {
  const params = new URLSearchParams({
    post_logout_redirect_uri: AUTHENTIK_CONFIG.postLogoutRedirectUri,
  });

  // Include id_token_hint if available for better logout experience
  if (idToken) {
    params.set('id_token_hint', idToken);
  }

  const logoutUrl = `${AUTHENTIK_CONFIG.endSessionEndpoint}?${params.toString()}`;
  window.location.href = logoutUrl;
}

/**
 * True only on a local development host. The Authentik-only "local dev" session
 * (no Supabase session) must never be used anywhere else: without a Supabase
 * session every query runs as anon and RLS returns empty tables.
 */
export function isLocalDevHost() {
  const host = window.location.hostname;
  return host === 'localhost' || host === '127.0.0.1' || host === '[::1]';
}

/**
 * Check if user is on the OAuth callback page
 */
export function isOnCallbackPage() {
  return window.location.pathname === '/callback';
}

/**
 * Parse OAuth callback parameters from URL
 */
export function parseCallbackParams() {
  const params = new URLSearchParams(window.location.search);
  return {
    code: params.get('code'),
    state: params.get('state'),
    error: params.get('error'),
    errorDescription: params.get('error_description'),
  };
}
